const _ = require("lodash");
const StateReset = require("../../core/vault/StateReset");
const Slots = require("../../core/vault/Slots");
const OpsUser = require("../../core/vault/OpsUser");
const OpsPage = require("../../core/vault/OpsPage");
const OpsCart = require("../vault/OpsCart");
const OpsMarketing = require("../../core/vault/OpsMarketing");
const Analytics = require("../../core/util/Analytics");
const UpdateStore = require("./UpdateStore");
const URLMake = require("../../../shared/URLMake");
const OfficePage = require("./OfficePage");
const {
	isInProgressPrimarySetupStepV2,
	getRestoredV2Step,
} = require("../../parent/util/PrimaryRegistrationUtilV2");
const {
	getLastPrimarySetupStepV2,
} = require("../../parent/util/primarySetupStepStorageV2");

const AT = require("../../core/vault/ActionTypes");
const {
	atUser,
	atPage,
	atCart,
	atOfficeFlag,
	atParentAccount,
} = require("../../core/vault/StateCursors");
const {setIn, extendIn} = require("../../../shared/ImmHelpers");

// Used as a reset hook.
const doOfficeStateReset = function(state, initState, now) {
	state = extendIn(state, [], {
		cart: OpsCart.clearNonItems(atCart(initState).get(), now),
		office: {
			contact: false,
			email: false,
		},
	});
	return state;
};

//Compute the primary setup and secondary registration steps
const setSetupSteps = s => {
	s.primarySetupStep = "START";
	s.registrationStep = "welcome";
	if (s.hasCompletedRegistration) {
		s.primarySetupStep = "FINAL";
		if (!s.customerSegment) {
			return;
		} else if (!s.parentLockSettings) {
			s.registrationStep = "lockScheme";
		} else {
			s.registrationStep = "final";
		}
	}
};

const actionHandlers = {
	// Note that this may run on a state object that already ran this action on
	// the server, so avoid resetting what's already there.
	[AT.OFFICE_INITIAL](vault, state, action) {
		const now = vault.clock.now();
		state = doOfficeStateReset(state, state, now);
		StateReset.addResetHook(vault, doOfficeStateReset);

		state = UpdateStore.forOfficeInitial(vault, state);
		// Fix checkout steps after everything has loaded
		state = UpdateStore.fixCheckoutPageStep(state);
		return state;
	},

	[AT.START_CHANGE_PASSWORD](vault, state, action) {
		vault.emitEvent("beginChangePassword", action.opts);
		return state;
	},

	[AT.START_SWITCH_ACCOUNT](vault, state, action) {
		const user = atUser(state).get();
		const userModel = _.get(user, ["model"], {});
		let suggestedPage = URLMake.state.forHome();
		if (OpsUser.hasParentRole(user)) {
			suggestedPage = URLMake.state.forParent();
		} else if (OpsUser.hasRole(user, "teacher")) {
			suggestedPage = URLMake.state.forSchool();
		}
		vault.emitEvent("showOfficeModal", "switchAccount", {
			displayName: userModel.email || userModel.displayName,
			suggestedPage,
		});
		return state;
	},

	[AT.SHOW_OFFICE_MESSAGE](vault, state, action) {
		vault.emitEvent("showOfficeModal", action.messageType, action.extraInfo);
		return state;
	},

	//TODO: The following email & contact actions follow basically the exact same flow.
	//We may want to try to do more code reuse.

	[AT.SUBMIT_CONTACT_FORM_REQUEST](vault, state, action) {
		if (atOfficeFlag(state, "contact").get()) {
			//Already submitting a contact form
			return;
		}
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal", "submitContact");
			return state;
		}

		const previousPage = URLMake.toURL(_.get(state, "previousPage"));
		const userID = _.get(state, "user.userID");

		//Send diagnostic data in case the customer is reporting a technical issue
		const diagnostics = vault.getDiagnostics();
		delete diagnostics.ongoingAction; //No need to record since it's this action
		vault.api.submitContactForm(
			{
				...action.form,
				previousPage,
				userID,
			},
			diagnostics
		);
		vault.emitEvent("contactRequestStarted");
		return atOfficeFlag(state, "contact").set(true);
	},

	[AT.SUBMIT_CONTACT_FORM_SUCCESS](vault, state, action) {
		state = atOfficeFlag(state, "contact").set(false);
		if (action.data.mailingListID) {
			state = OpsMarketing.addEventToQueue(
				state,
				"MailingListSignUp",
				action.data.mailingListID,
				vault.cookie,
				vault.throttler,
				vault.clock
			);
		}
		vault.emitEvent("contactRequestComplete", true);
		return state;
	},

	[AT.SUBMIT_CONTACT_FORM_FAIL](vault, state, action) {
		state = atOfficeFlag(state, "contact").set(false);
		vault.emitEvent("contactRequestComplete", false);
		return state;
	},

	[AT.BEGIN_EMAIL_SIGN_UP](vault, state, action) {
		vault.emitEvent("beginEmailSignUp", action.email);
		return state;
	},

	[AT.EMAIL_SIGN_UP_REQUEST](vault, state, action) {
		if (atOfficeFlag(state, "email").get()) {
			//Already submitting an email sign up
			return;
		}
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal", "emailSignUp");
			return state;
		}
		vault.api.emailSignUp(action.email, action.role);
		vault.emitEvent("emailSignUpStarted");
		return atOfficeFlag(state, "email").set(true);
	},

	[AT.EMAIL_SIGN_UP_SUCCESS](vault, state, action) {
		state = atOfficeFlag(state, "email").set(false);
		vault.emitEvent("emailSignUpComplete", "signUpSuccess");
		Analytics.sendEmailSignup();
		state = OpsMarketing.addEventToQueue(
			state,
			"MailingListSignUp",
			action.data.mailingListID,
			vault.cookie,
			vault.throttler,
			vault.clock
		);
		return state;
	},

	[AT.EMAIL_SIGN_UP_FAIL](vault, state, action) {
		state = atOfficeFlag(state, "email").set(false);
		if (action.error === "E_TOO_MANY_REQUESTS") {
			vault.emitEvent("emailSignUpEnd");
			return state;
		}
		let nextStep = "signUpFail";
		if (action.error === "E_DUPLICATE_EMAIL") {
			nextStep = "duplicateEmail";
		}
		vault.emitEvent("emailSignUpComplete", nextStep);
		return state;
	},

	[AT.SIGN_UP_ADDITIONAL_EMAIL_RECIPIENTS_REQUEST](vault, state, action) {
		vault.api.emailSignUpAdditionalRecipients(
			action.addEmails,
			action.removeEmails,
			action.studentID,
			action.userID
		);
		vault.emitEvent("additionalEmailSignUpStarted");
		return state;
	},
	[AT.SIGN_UP_ADDITIONAL_EMAIL_RECIPIENTS_SUCCESS](vault, state, action) {
		const emails = _.get(action, "data.emails");
		const studentID = _.get(action, "payload.studentID");
		const parentAccount = atParentAccount(state).get();
		const studentIndex = _.findIndex(
			parentAccount.students,
			s => s.userID === studentID
		);
		state = atParentAccount(state).setIn(
			["students", studentIndex, "additionalEmails"],
			emails
		);
		vault.emitEvent("additionalEmailSignUpComplete", "success");
		return state;
	},
	[AT.SIGN_UP_ADDITIONAL_EMAIL_RECIPIENTS_FAIL](vault, state, action) {
		vault.emitEvent("additionalEmailSignUpComplete", "failure");
		return state;
	},

	[AT.LOAD_TEACHER_CLASSES_SLOT_REQUEST](vault, state, action) {
		const slot = Slots.table.teacherClasses();
		return Slots.loadStart(state, slot, vault.api.loadSlot);
	},
	[AT.LOAD_TEACHER_CLASSES_SLOT_SUCCESS](vault, state, action) {
		return Slots.loadSuccess(state, action.slot, value => {
			return extendIn(value, [], _.pick(action.data, ["classes", "perms"]));
		});
	},
	[AT.LOAD_TEACHER_CLASSES_SLOT_FAIL](vault, state, action) {
		return Slots.loadError(state, action.slot, action.error);
	},

	[AT.LOAD_TEACHER_CLASS_DATA_SLOT_REQUEST](vault, state, action) {
		const slot = Slots.table.teacherClassData(action.classID);
		return Slots.loadStart(state, slot, vault.api.loadSlot);
	},
	[AT.LOAD_TEACHER_CLASS_DATA_SLOT_SUCCESS](vault, state, action) {
		state = Slots.loadSuccess(state, action.slot, value => {
			value = extendIn(
				value,
				[],
				_.pick(action.data, ["classInfo", "students", "perms", "blockNames"])
			);
			return value;
		});
		state = atUser(state).set(u => OpsUser.setStudentsFromSlot(u, action.data));
		return state;
	},
	[AT.LOAD_TEACHER_CLASS_DATA_SLOT_FAIL](vault, state, action) {
		return Slots.loadError(state, action.slot, action.error);
	},

	[AT.LOAD_TEACHER_CLASS_DATA_CHAPTERS_SLOT_REQUEST](vault, state, action) {
		const slot = Slots.table.teacherClassDataChapters(
			action.classID,
			action.chapterIDs
		);
		if (action.force) {
			state = Slots.loadRestart(state, slot);
		}
		return Slots.loadStart(state, slot, vault.api.loadSlot);
	},
	[AT.LOAD_TEACHER_CLASS_DATA_CHAPTERS_SLOT_SUCCESS](vault, state, action) {
		state = Slots.loadSuccess(state, action.slot);
		state = loadStudentChapters(vault, state, action.data, action.payload);
		state = atUser(state).set(u => OpsUser.setStudentsFromSlot(u, action.data));
		return state;
	},
	[AT.LOAD_TEACHER_CLASS_DATA_CHAPTERS_SLOT_FAIL](vault, state, action) {
		return Slots.loadError(state, action.slot, action.error);
	},

	[AT.LOAD_PARENT_DATA_SLOT_REQUEST](vault, state, action) {
		const slot = Slots.table.parentData();
		return Slots.loadStart(state, slot, vault.api.loadSlot);
	},
	[AT.LOAD_PARENT_DATA_SLOT_SUCCESS](vault, state, action) {
		state = Slots.loadSuccess(state, action.slot, value => {
			const newData = _.pick(action.data, ["students", "blockNames"]);
			_.forEach(newData.students, (s, studentID) => {
				s.studentID = parseInt(studentID) || studentID;
				const existingStep = value?.students?.[studentID]?.primarySetupStep;
				setSetupSteps(s);
				if (!s.hasCompletedRegistration) {
					if (isInProgressPrimarySetupStepV2(existingStep)) {
						// Mid-session reload: preserve the exact in-progress step.
						s.primarySetupStep = existingStep;
					} else {
						// Fresh page load: prefer the per-device last-step record so
						// refresh keeps the parent on the step they were on. If the
						// device has no record for this student, force WELCOME (unless
						// the server says they haven't started at all) so they always
						// see the intro before continuing.
						const storedStep = getLastPrimarySetupStepV2(studentID);
						if (storedStep) {
							s.primarySetupStep = storedStep;
						} else {
							const derived = getRestoredV2Step(
								s.registrationStatus,
								s.displayName
							);
							s.primarySetupStep = derived === "START" ? "START" : "WELCOME";
						}
					}
				}
			});
			value = extendIn(value, [], newData);
			return value;
		});
		state = atUser(state).set(u => OpsUser.setStudentsFromSlot(u, action.data));
		return state;
	},
	[AT.LOAD_PARENT_DATA_SLOT_FAIL](vault, state, action) {
		return Slots.loadError(state, action.slot, action.error);
	},

	[AT.LOAD_PARENT_DATA_CHAPTERS_SLOT_REQUEST](vault, state, action) {
		const slot = Slots.table.parentDataChapters(
			action.chapterIDs,
			action.studentIDs,
			action.classID
		);
		if (action.force) {
			state = Slots.loadRestart(state, slot);
		}
		return Slots.loadStart(state, slot, vault.api.loadSlot);
	},
	[AT.LOAD_PARENT_DATA_CHAPTERS_SLOT_SUCCESS](vault, state, action) {
		state = Slots.loadSuccess(state, action.slot);
		state = loadStudentChapters(vault, state, action.data, action.payload);
		state = atUser(state).set(u => OpsUser.setStudentsFromSlot(u, action.data));
		return state;
	},
	[AT.LOAD_PARENT_DATA_CHAPTERS_SLOT_FAIL](vault, state, action) {
		return Slots.loadError(state, action.slot, action.error);
	},

	[AT.LOAD_CONTENT_REPORT_SLOT_REQUEST](vault, state, action) {
		const slot = Slots.table.contentReport(action.classID, action.chapterID);
		return Slots.loadStart(state, slot, vault.api.loadSlot);
	},
	[AT.LOAD_CONTENT_REPORT_SLOT_SUCCESS](vault, state, action) {
		return Slots.loadSuccess(state, action.slot, value => {
			value = extendIn(
				value,
				[],
				_.pick(action.data, ["blockIDs", "blockData"])
			);
			return value;
		});
	},
	[AT.LOAD_CONTENT_REPORT_SLOT_FAIL](vault, state, action) {
		return Slots.loadError(state, action.slot, action.error);
	},
};

const loadStudentChapters = function(vault, state, response, payload) {
	const path = payload.classID
		? ["officeStudentData", payload.classID, "students"]
		: ["officeStudentData", "students"];
	return setIn(state, path, sd => {
		_.each(response.students, (studentData, sid) => {
			sd = extendIn(sd, [sid, "byChapter"], studentData.byChapter);
		});
		return sd;
	});
};

//
// Post update hooks
//

const augmentUserLoad = function(vault, oldState, newState, action) {
	// TODO: The need to check for OFFICE_INITIAL here is annoying, but needed
	// due to these two actions possibly happening in either order (and
	// LOAD_USER_SUCCESS can happen on the server). How can we make
	// initialization more stable?
	if (
		action.type !== AT.LOAD_USER_SUCCESS &&
		action.type !== AT.OFFICE_INITIAL
	) {
		return newState;
	}
	const user = atUser(newState).get();
	const page = atPage(newState).get();
	// If Analytics is active, we're about to reload, so suppress this modal.
	if (
		OpsUser.isStudent(user) &&
		OpsPage.isOfficePage(page) &&
		!OpsPage.isPageProtected(page) &&
		!OpsPage.isPageForLogoutPrompt(page) &&
		!Analytics.isActive() &&
		!OpsPage.isHomeMain(page)
	) {
		if (OpsUser.hasRole(user, "student deactivated")) {
			vault.emitEvent("showOfficeModal", "studentDeactivated", {
				displayName: user.model.displayName,
			});
		} else {
			vault.emitEvent("showOfficeModal", "student", {
				displayName: user.model.displayName,
			});
		}
	}
};

const validateOfficePage = function(vault, oldState, newState, action) {
	const user = atUser(newState).get();
	const page = atPage(newState).get();
	// TODO: do we need to move this to util? Trainer checks do, but this is simpler.

	if (!OpsUser.isLoggedIn(user) || !user.model) return newState;

	// If the page requires the user to have specific permissions (ex.
	// parent/teacher report pages), check that the user has those correct
	// permissions.
	if (OpsPage.isParentPage(page)) {
		if (
			_.get(page, "pageData.studentID") &&
			!OpsUser.hasKeyForRef(
				user,
				"student view",
				_.get(page, "pageData.studentID")
			)
		) {
			// Cannot view a particular student, then redirect to main parent page.
			newState = atPage(newState).set(URLMake.state.forParent());
			return newState;
		}
	}
	if (OpsPage.isReportPage(page)) {
		// Keep the pageData around so switching progress/activity tabs don't
		// lose lesson/date info
		const keepProps = [
			"grade",
			"chapter",
			"block",
			"bookTest",
			"date",
			"typeID",
			"labBlockID",
		];
		const oldPageData = atPage(oldState).getIn("pageData");
		const newPageData = atPage(newState).getIn("pageData");
		if (OfficePage.isValidReportPageData(newPageData)) {
			const mergedPageData = _.extend(
				_.pick(oldPageData, keepProps),
				newPageData
			);
			newState = atPage(newState).extendIn("pageData", mergedPageData);
		} else {
			const classID = newPageData.classID;
			if (newPageData.studentID) {
				newState = atPage(newState).set(URLMake.state.forParent());
			} else if (classID) {
				newState = atPage(newState).set(URLMake.state.forClassReport(classID));
			}
		}
	}

	return newState;
};

//
// Exported functions to augment vault update
//

const augment = function(updater) {
	updater.addActionHandler(actionHandlers);

	updater.addPostUpdateHook(augmentUserLoad, "office:augmentUserLoad");
	updater.addPostUpdateHook(
		validateOfficePage,
		"office:validateOfficePage",
		-500
	);

	UpdateStore.augment(updater);
};

module.exports = {
	augment,
};
